import { Ref, useEffect, useState } from 'react';
import { useTranslations } from '~/services/umbraco/hooks';
import { AccordionItem, AccordionItemRef } from '~/shared/components/Accordion';
import {
    LinkText,
    SustainabilityIcon,
    SustainabilityValueHeadline,
    SustainabilityValue,
    SustainabilityWrapper,
    Text,
} from '../styled';
import { getImageProps } from 'next/image';
import { theme } from '~/theme';
import { umbracoLoader } from '~/shared/components/Image/loaders/umbracoLoader';
import { ImgProps } from 'next/dist/shared/lib/get-img-props';

const SustainabilityValuesDescription = ({
    sustainabilityValues,
    ref,
}: {
    sustainabilityValues?: string[];
    ref?: Ref<AccordionItemRef>;
}) => {
    const { translate } = useTranslations();
    const [sustainabilityTranslations, setSustainabilityTranslations] = useState<
        SustainabilityTranslations[] | undefined
    >();

    useEffect(() => {
        if ((sustainabilityValues?.length || 0) > 0) {
            const translations: SustainabilityTranslations[] | undefined = sustainabilityValues
                ?.map((value) => {
                    const translations = {
                        headline: translate(`sustainability.${labelName(value)}.headline`),
                        description: translate(`sustainability.${labelName(value)}.description`),
                        link: translate(`sustainability.${labelName(value)}.linkUrl`),
                        linkText: translate(`sustainability.${labelName(value)}.linktext`),
                        symbol: translate(`sustainability.${labelName(value)}.symbol`),
                    } as SustainabilityTranslations;

                    const {
                        props: { ...sustainabilityIconProps },
                    } = getImageProps({
                        loader: umbracoLoader,
                        src: translations.symbol ?? '',
                        alt: translations.headline,
                        sizes: `50px`,
                        width: theme.space['7'].replace('px', '') as unknown as number,
                        height: theme.space['7'].replace('px', '') as unknown as number,
                        loading: 'lazy',
                        style: {
                            objectFit: 'cover',
                        },
                    });

                    translations.sustainabilityIcon = sustainabilityIconProps;

                    return translations;
                })
                .filter((item) => !!item.headline);

            setSustainabilityTranslations(translations);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sustainabilityValues]);

    return !sustainabilityTranslations?.length ? null : (
        <AccordionItem
            ref={ref}
            id="sustainabilityValuesDescription"
            includeId
            header={translate('product.sustainabilityValues')}
        >
            <SustainabilityWrapper>
                {sustainabilityTranslations?.map((value, index) => (
                    <SustainabilityValue key={index}>
                        <SustainabilityIcon>
                            {value.symbol ? (
                                <picture>
                                    <source media={''} srcSet={value.sustainabilityIcon?.srcSet} />
                                    {value.sustainabilityIcon && (
                                        <source srcSet={value.sustainabilityIcon.srcSet} />
                                    )}
                                    <img {...value.sustainabilityIcon} alt={value.headline} />
                                </picture>
                            ) : null}
                        </SustainabilityIcon>

                        <div>
                            <SustainabilityValueHeadline isTitle>
                                {value.headline}
                            </SustainabilityValueHeadline>

                            {value.description ? <Text>{value.description}</Text> : null}

                            {value.link === undefined ? null : (
                                <LinkText href={value.link} target="_blank">
                                    {value.linkText}
                                </LinkText>
                            )}
                        </div>
                    </SustainabilityValue>
                ))}
            </SustainabilityWrapper>
        </AccordionItem>
    );
};

export default SustainabilityValuesDescription;

const labelName = (value?: string): string | undefined =>
    value
        ?.replaceAll('æ', 'ae')
        .replaceAll('ø', 'oe')
        .replaceAll('å', 'aa')
        .replaceAll(/[^0-9a-z-A-Z ]/g, '');

type SustainabilityTranslations = {
    headline: string;
    description: string;
    link?: string;
    linkText?: string;
    symbol?: string;
    sustainabilityIcon?: ImgProps;
};
